/* The Veo half of the ad-video flow: submit one job per aspect ratio, poll
   each to completion, and let the user save or regenerate the results one at
   a time. Everything before this — the brief, the references, the prompt —
   belongs to the panel; this hook only knows how to run a batch. */
import { useRef, useState, type MutableRefObject } from "react"
import { toast } from "react-toastify"
import {
  useAdForgeGenerateMotionMutation,
  useAdForgeProxyVideoMutation,
  useAdForgeUploadMotionVideoMutation,
  useLazyAdForgeMotionStatusQuery,
} from "~/ui-rtk/api/adForgeApi"
import { useSetAssetThumbnailMutation } from "~/ui-rtk/api/assetsApi"
import { useAppSelector } from "~/ui-rtk/app/hooks"
import { selectAdForgeProductContext } from "~/ui-rtk/app/selectors/ad-forge.selector"
import {
  captureVideoFrameFromUrl,
  fileToDataUrl,
} from "~/ui-rtk/utils/generate-thumbnail"
import { assetToScrapedImage, type ScrapedImage } from "../../../types"
import { resolveProductRef } from "../../../utils/product-ref"
import { REFERENCE_MODE_DURATION } from "../constants"
import mediaRef from "../media-ref"
import type {
  PendingResult,
  VeoDuration,
  VeoRatio,
  VeoResolution,
} from "../types"

// Backend stores in-flight operations in memory and can briefly return
// 404 ("Operation X not found") around warm restarts. Tolerate up to 5
// consecutive 404s on the same operationId before giving up.
const POLL_INTERVAL_MS = 8000 // bumped from 5s — see backend throttle note
const MAX_POLLS = Math.ceil((10 * 60 * 1000) / POLL_INTERVAL_MS) // ~10min
const MAX_NOT_FOUND_RETRIES = 5

interface UseMotionPipelineParams {
  /** The hero product shot. Also the parent asset saved clips hang off. */
  asset: ScrapedImage
  /** Ratios the current run covers (the output aspect-ratio selector). */
  selectedVeoRatios: { ratio: VeoRatio; label: string }[]
  durationSeconds: VeoDuration
  resolution: VeoResolution
  productContext: string
  brandContext: string
  /** Shared with the compose pipeline — flipped when the panel unmounts or
   *  the user cancels, so an in-flight poll stops writing state. */
  cancelledRef: MutableRefObject<boolean>
  setStatus: (status: "idle" | "composing" | "submitting") => void
  setProgressDone: (done: number) => void
  onAssetReady?: (asset: ScrapedImage) => Promise<void> | void
  /** The panel's current motion prompt, read lazily — used when a per-card
   *  regenerate has neither an override nor a stored batch prompt. */
  buildFallbackPrompt: () => string
}

export default function useMotionPipeline({
  asset,
  selectedVeoRatios,
  durationSeconds,
  resolution,
  productContext,
  brandContext,
  cancelledRef,
  setStatus,
  setProgressDone,
  onAssetReady,
  buildFallbackPrompt,
}: UseMotionPipelineParams) {
  // Per-ratio generation outcomes. Populated by runBatch; the user reviews
  // each entry and decides to save (upload to library) or regenerate.
  const [pendingResults, setPendingResults] = useState<PendingResult[]>([])
  // Source + prompt + references the current batch ran with — a per-card
  // Regenerate reuses them so a single retry matches its siblings.
  const [lastMotionPrompt, setLastMotionPrompt] = useState("")
  const motionSourcesRef = useRef<Record<VeoRatio, string> | null>(null)
  const motionReferencesRef = useRef<string[]>([])

  const [generateMotion] = useAdForgeGenerateMotionMutation()
  const [fetchStatus] = useLazyAdForgeMotionStatusQuery()
  const [uploadMotionVideo] = useAdForgeUploadMotionVideoMutation()
  const [proxyVideo] = useAdForgeProxyVideoMutation()
  const [setAssetThumbnail] = useSetAssetThumbnailMutation()

  // Which feed product this session is about (NOT the `productContext` prompt
  // string above — that's the flattened product-facts blob). A Mega Feed
  // -seeded hero's synthetic id wins, else the session's product context id.
  // Sent with the save so the clip lands in that product's feed pool.
  const feedProductContext = useAppSelector(selectAdForgeProductContext)
  const productRefId = resolveProductRef(asset, feedProductContext?.productId)

  const hasActiveItem = pendingResults.some(
    r => r.status === "generating" || r.status === "saving",
  )
  const failedCount = pendingResults.filter(r => r.status === "failed").length
  const allFailed =
    pendingResults.length > 0 && failedCount === pendingResults.length

  const pollOne = async (
    ratio: VeoRatio,
    operationId: string,
  ): Promise<string> => {
    let notFoundCount = 0
    for (let i = 0; i < MAX_POLLS; i++) {
      if (cancelledRef.current) throw new Error("Cancelled")
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
      if (cancelledRef.current) throw new Error("Cancelled")
      let data
      try {
        data = await fetchStatus({ operationId }).unwrap()
      } catch (err) {
        const httpStatus = (err as { status?: number })?.status
        if (httpStatus === 404) {
          notFoundCount += 1
          if (notFoundCount >= MAX_NOT_FOUND_RETRIES) {
            throw new Error(
              `Operation not found after ${MAX_NOT_FOUND_RETRIES} retries (${ratio})`,
            )
          }
          continue
        }
        throw err
      }
      notFoundCount = 0
      if (data.status === "failed") {
        throw new Error(data.error || `Generation failed (${ratio})`)
      }
      if (data.status === "completed" && data.videoUris?.length) {
        return data.videoUris[0]
      }
    }
    throw new Error(`Generation timed out (${ratio})`)
  }

  // Submit + poll a single ratio and return a browser-playable preview URL.
  // Throws on cancellation/failure so callers can convert to a "failed" entry.
  const generateOne = async (
    ratio: VeoRatio,
    sourceAssetId: string,
    motionPrompt: string,
    references: string[],
  ): Promise<PendingResult> => {
    // Every categorized shot goes to Veo, not just the first frame: the
    // lifestyle, on-model, detail and packshot references all inform the motion
    // instead of being flattened into one still beforehand. Product and brand
    // direction ride along so the model knows what it is filming and how the
    // brand should look.
    const motionReferences = references.filter(v => v !== sourceAssetId)

    const res = await generateMotion({
      assetId: sourceAssetId,
      ...(motionReferences.length > 0 && {
        referenceAssetIds: motionReferences,
      }),
      ...(productContext && { productContext }),
      ...(brandContext && { brandContext }),
      prompt: motionPrompt,
      aspectRatio: ratio,
      // reference_to_video is 8s-only — the server clamps this anyway, but
      // sending the real value keeps the request honest.
      durationSeconds:
        motionReferences.length > 0 ? REFERENCE_MODE_DURATION : durationSeconds,
      resolution,
      model: "veo-3.1-fast-generate-001",
      enhancePrompt: true,
      personGeneration: "allow_adult",
      numberOfVideos: 1,
    }).unwrap()
    const videoUri = await pollOne(ratio, res.operationId)
    if (cancelledRef.current) throw new Error("Cancelled")
    // Veo returns either a data: URL (inline base64) or a gs://-style URI.
    // data: URLs are already playable in the browser, so use them directly.
    // For gs:// / https GCS URIs we need the server proxy to attach the
    // OAuth token + materialize the bytes. Upload only happens later for
    // the ratios the user keeps.
    let previewUrl: string
    if (videoUri.startsWith("data:")) {
      previewUrl = videoUri
    } else {
      const { videoUrl } = await proxyVideo({ videoUri }).unwrap()
      previewUrl = videoUrl
    }
    return { ratio, status: "success", videoUri, previewUrl }
  }

  // Run the per-ratio batch against one motion source. Process each ratio
  // fully in series: parallel submission trips Veo's per-project
  // concurrency limit (429 ThrottlerException).
  const runBatch = async (
    sources: Record<VeoRatio, string>,
    motionPrompt: string,
    references: string[],
  ) => {
    const batch = selectedVeoRatios
    motionSourcesRef.current = sources
    motionReferencesRef.current = references
    setLastMotionPrompt(motionPrompt)
    setPendingResults(
      batch.map(({ ratio }) => ({ ratio, status: "generating" as const })),
    )
    setStatus("submitting")
    let succeeded = 0
    let failed = 0
    for (let i = 0; i < batch.length; i++) {
      if (cancelledRef.current) break
      const { ratio } = batch[i]
      try {
        const result = await generateOne(
          ratio,
          sources[ratio],
          motionPrompt,
          references,
        )
        if (cancelledRef.current) break
        setPendingResults(prev =>
          prev.map((r, idx) => (idx === i ? result : r)),
        )
        succeeded += 1
      } catch (e) {
        if (cancelledRef.current) break
        const errMsg = e instanceof Error ? e.message : "Generation failed"
        setPendingResults(prev =>
          prev.map((r, idx) =>
            idx === i ? { ratio, status: "failed", error: errMsg } : r,
          ),
        )
        failed += 1
      }
      setProgressDone(succeeded + failed)
    }
    if (cancelledRef.current) return
    if (succeeded === 0) {
      toast.error("All variants failed. Try again or adjust the brief.")
    } else if (failed > 0) {
      toast.warning(
        `${succeeded} of ${batch.length} generated. Review failures below and regenerate.`,
      )
    } else {
      toast.success(
        `${succeeded} variant${succeeded === 1 ? "" : "s"} ready — review and save.`,
      )
    }
  }

  const handleSaveOne = async (index: number) => {
    const target = pendingResults[index]
    if (!target || target.status !== "success" || !target.videoUri) return
    setPendingResults(prev =>
      prev.map((r, idx) => (idx === index ? { ...r, status: "saving" } : r)),
    )
    try {
      const savedAsset = await uploadMotionVideo({
        videoUri: target.videoUri,
        // A feed-seeded hero carries only a URL, so there is no library id to
        // hang the clip off. parentAssetId is optional server-side, so save it
        // unparented (losing lineage) rather than dropping the generation —
        // this used to bail silently and the Save button just did nothing.
        ...(asset.assetId && { parentAssetId: asset.assetId }),
        aspectRatio: target.ratio,
        metadata: { generatedBy: "ai_motion", aspectRatio: target.ratio },
        ...(productRefId && { productRefId }),
      }).unwrap()
      // Best-effort cover frame: the server-side upload has no client File to
      // frame-grab from, so capture one from the hosted copy and store it as
      // the asset's thumbnail. Never fails the save — a coverless video is
      // still a saved video.
      let readyAsset = savedAsset
      if (savedAsset.url) {
        try {
          let frame = await captureVideoFrameFromUrl(savedAsset.url)
          if (!frame && !savedAsset.url.startsWith("data:")) {
            // Direct capture needs CORS on the asset host; when it fails,
            // re-fetch the bytes through the backend proxy (data: URL —
            // origin-clean) and capture from that instead.
            const { videoUrl } = await proxyVideo({
              videoUri: savedAsset.url,
            }).unwrap()
            frame = await captureVideoFrameFromUrl(videoUrl)
          }
          if (frame) {
            const imageBase64 = await fileToDataUrl(frame)
            readyAsset = await setAssetThumbnail({
              assetId: savedAsset.id,
              imageBase64,
            }).unwrap()
          }
        } catch {
          // Leave the clip without a thumbnail.
        }
      }
      await onAssetReady?.(assetToScrapedImage(readyAsset))
      setPendingResults(prev =>
        prev.map((r, idx) => (idx === index ? { ...r, status: "saved" } : r)),
      )
      toast.success(
        productRefId
          ? `${target.ratio} saved to the product's feed pool`
          : `${target.ratio} saved to your pool`,
      )
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : "Save failed"
      setPendingResults(prev =>
        prev.map((r, idx) =>
          idx === index ? { ...r, status: "success", error: errMsg } : r,
        ),
      )
      toast.error(errMsg)
    }
  }

  const handleRegenerateOne = async (
    index: number,
    promptOverride?: string,
  ) => {
    const target = pendingResults[index]
    if (!target) return
    if (target.status === "saving" || target.status === "generating") return
    // Fall back through mediaRef, not asset.assetId: a feed-seeded hero has
    // only a URL, so the id fallback resolves to undefined and the guard below
    // would swallow the click with no feedback whenever the batch's source map
    // is missing (a remount, or a regenerate racing the first run).
    const sourceId = motionSourcesRef.current?.[target.ratio] ?? mediaRef(asset)
    if (!sourceId) return
    const motionPrompt =
      promptOverride?.trim() || lastMotionPrompt || buildFallbackPrompt()
    setPendingResults(prev =>
      prev.map((r, idx) =>
        idx === index ? { ratio: target.ratio, status: "generating" } : r,
      ),
    )
    try {
      const result = await generateOne(
        target.ratio,
        sourceId,
        motionPrompt,
        motionReferencesRef.current,
      )
      setPendingResults(prev =>
        prev.map((r, idx) => (idx === index ? result : r)),
      )
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : "Generation failed"
      setPendingResults(prev =>
        prev.map((r, idx) =>
          idx === index
            ? { ratio: target.ratio, status: "failed", error: errMsg }
            : r,
        ),
      )
      toast.error(errMsg)
    }
  }

  return {
    pendingResults,
    setPendingResults,
    hasActiveItem,
    failedCount,
    allFailed,
    lastMotionPrompt,
    runBatch,
    handleSaveOne,
    handleRegenerateOne,
  }
}
